/**
 * AccountPage — Phase 6 rebuild (2026-05-30).
 *
 * Four sections matching the locked /preview/dashboard/account/ spec:
 *   1. Profile — view-with-Edit toggle around BusinessProfileForm
 *   2. Plan — reads me.plan_tier (Free/Pro) + /api/usage progress bar +
 *      "Grow into Pro" CTA
 *   3. Notifications — 3 checkboxes persisted in localStorage. Backend
 *      per-user prefs flagged as gap (Phase 6 backend follow-on item 2);
 *      pairs with Resend wiring when that lands.
 *   4. Danger zone — Export all contacts (cursor-loop fetch + CSV),
 *      Sign out, Delete account (disabled until backend route ships;
 *      flagged as gap).
 *
 * Drops the old SettingsPage shape entirely:
 *   - Profile tab → Profile section here
 *   - Install tab → dropped (Snippet lives on Integrations from Phase 5)
 *   - Account tab → folded into Danger zone (Sign out) + this page's
 *     email display in Profile section
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signOut } from 'firebase/auth';
import { Download, Pencil } from 'lucide-react';

import { auth } from '@/lib/firebase';
import { useAuth } from '@/lib/useAuth';
import { apiRequest, ApiError } from '@/lib/api';
import {
  BusinessProfileForm,
  type BusinessProfileFormValues,
} from '@/components/forms/BusinessProfileForm';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHeader } from '@/components/PageHeader';
import { SkeletonBox, SkeletonGroup } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';
import { exportToCsv } from '@/lib/contacts';
import type { Contact } from '@/types/contacts';
import {
  useUpgradeCheckout,
  type BillingInterval,
} from '@/lib/useUpgradeCheckout';
import { UsageBar } from '@/components/usage/UsageBar';
import { usageTier, USAGE_TEXT } from '@/lib/usage';

interface BusinessProfileResponse {
  business_name: string;
  owner_phone: string;
  auto_reply_text: string;
  website_url: string;
  timezone: string;
  currency: string;
}

interface UsageResponse {
  effective_tier: 'pro' | 'trial' | 'free';
  trial_ends_at: string | null;
  count: number;
  limit: number | null;
  locked_count: number;
  not_sent_total: number;
  recoverable_count: number;
  period_start: string;
}

// Whole days until an ISO deadline (floored at 0) — the trial countdown.
function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

interface SubscriptionResponse {
  plan_tier: string;
  plan_interval: string | null;
  payment_status: string | null;
}

interface PortalResponse {
  cancel_url: string | null;
  update_payment_url: string | null;
  cancels_at: string | null;
  renews_at: string | null;
}

interface ContactsPage {
  items: Contact[];
  next_cursor: string | null;
}

// Pro price by interval. Marketing copy — Paddle's overlay shows the authoritative
// localized price + tax. One-line edit if prices shift (June 2026 restructure).
const PRO_PRICE: Record<BillingInterval, string> = { monthly: '$29', yearly: '$290' };
const PRO_PERIOD: Record<BillingInterval, string> = { monthly: '/month', yearly: '/year' };
const PLAN_BLURB: Record<'free' | 'pro', string> = {
  free: 'For getting started',
  pro: 'Unlimited contacts',
};

// LocalStorage key for notification preferences. Backend per-user prefs are
// the flagged Phase 6 backend follow-on item 2 — migrate when that lands.
const NOTIF_PREFS_KEY = 'heymotek_notif_prefs_v1';

interface NotifPrefs {
  new_contact: boolean;
  monthly_summary: boolean;
  product_updates: boolean;
}

const DEFAULT_PREFS: NotifPrefs = {
  new_contact: true,
  monthly_summary: true,
  product_updates: false,
};

function loadPrefs(): NotifPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(NOTIF_PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<NotifPrefs>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: NotifPrefs) {
  try {
    window.localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* localStorage may be unavailable (private mode, etc.); fail silent. */
  }
}

export function AccountPage() {
  const { me, viewAsClientId, loading: authLoading } = useAuth();

  // System admins view client data via View-as. Prefer impersonated id;
  // fall back to user's own membership.
  const clientId = viewAsClientId ?? me?.memberships?.[0]?.client_id;

  if (authLoading) return <AccountPageSkeleton />;

  if (!clientId) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <ErrorState
          title={
            me?.is_system_admin
              ? 'Pick a client from "View as" to see their account.'
              : 'No client linked to your account yet.'
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <PageHeader title="Account" subtitle="Your business profile, plan, and preferences." />

      <ProfileSection clientId={clientId} email={me?.email ?? ''} />
      <PlanSection />
      <NotificationsSection />
      <DangerZoneSection />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Profile section — view-with-Edit
// ──────────────────────────────────────────────────────────────────────

function ProfileSection({
  clientId,
  email,
}: {
  clientId: string;
  email: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const profileQuery = useQuery({
    queryKey: ['client', clientId, 'business-profile'],
    queryFn: () =>
      apiRequest<BusinessProfileResponse>(
        `/clients/${clientId}/business-profile`,
      ),
  });

  async function handleSubmit(values: BusinessProfileFormValues) {
    try {
      await apiRequest(`/clients/${clientId}/business-profile`, {
        method: 'PATCH',
        body: values,
      });
      queryClient.invalidateQueries({
        queryKey: ['client', clientId, 'business-profile'],
      });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      setEditing(false);
    } catch (e) {
      if (e instanceof ApiError) throw new Error(e.detail || e.message);
      throw e;
    }
  }

  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-extrabold text-dark sm:text-xl">Profile</h2>
        {!editing && profileQuery.data && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-info hover:underline"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        )}
      </div>

      {profileQuery.isLoading && (
        <SkeletonGroup className="space-y-3">
          {['w-48', 'w-40', 'w-56', 'w-44', 'w-36', 'w-32'].map((w, i) => (
            <div key={i} className="flex flex-col gap-1 sm:flex-row sm:gap-4">
              <SkeletonBox className="h-4 w-32 sm:w-40" />
              <SkeletonBox tone="prominent" className={`h-4 ${w}`} />
            </div>
          ))}
        </SkeletonGroup>
      )}

      {profileQuery.isError && (
        <ErrorState
          title="Couldn't load your profile"
          onRetry={() => profileQuery.refetch()}
        />
      )}

      {profileQuery.data && !editing && (
        <dl className="space-y-3">
          <Field
            label="Business name"
            value={profileQuery.data.business_name}
          />
          <Field label="Email" value={email} />
          <Field label="Website" value={profileQuery.data.website_url} />
          <Field label="Owner phone" value={profileQuery.data.owner_phone} />
          <Field label="Timezone" value={profileQuery.data.timezone} />
          <Field label="Currency" value={profileQuery.data.currency} />
        </dl>
      )}

      {profileQuery.data && editing && (
        <div className="space-y-3">
          <BusinessProfileForm
            initialValues={profileQuery.data}
            emailReadOnly={email}
            onSubmit={handleSubmit}
            submitLabel="Save changes"
            showDiscardButton
          />
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs font-semibold text-muted-foreground hover:underline"
          >
            Cancel without saving
          </button>
        </div>
      )}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="text-xs font-bold uppercase tracking-wider text-muted-foreground sm:w-40 sm:text-sm">
        {label}
      </dt>
      <dd className="text-base font-medium text-dark">{value || '—'}</dd>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Plan section
// ──────────────────────────────────────────────────────────────────────

function PlanSection() {
  const { me } = useAuth();
  const isPro = me?.plan_tier === 'pro';

  const usageQuery = useQuery({
    queryKey: ['usage'],
    queryFn: () => apiRequest<UsageResponse>('/usage'),
    staleTime: 30_000,
  });

  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-extrabold text-dark sm:text-xl">Plan</h2>
        <a
          href="https://heymotek.com/pricing/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-semibold text-info hover:underline"
        >
          See plans →
        </a>
      </div>

      {isPro ? (
        <ProPlanBody usageQuery={usageQuery} />
      ) : (
        <FreePlanBody usageQuery={usageQuery} />
      )}
    </section>
  );
}

function FreePlanBody({
  usageQuery,
}: {
  usageQuery: ReturnType<typeof useQuery<UsageResponse>>;
}) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('monthly');
  const [processing, setProcessing] = useState(false);
  const { configured, openCheckout } = useUpgradeCheckout({
    onCompleted: () => setProcessing(true),
  });

  // After checkout completes, the webhook flips plan_tier server-side. Poll
  // /me (bounded) until it lands; the parent then swaps in ProPlanBody.
  useEffect(() => {
    if (!processing || me?.plan_tier === 'pro') return;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      if (tries >= 20) window.clearInterval(id); // ~30s cap
    }, 1500);
    return () => window.clearInterval(id);
  }, [processing, me?.plan_tier, queryClient]);

  // Trial vs Free (post-trial) — PLAN_FREE_TIER_REDESIGN.md. Trial = everything
  // on, countdown only. Free = 100 visible contacts (escalation off the same
  // tier the bar uses) + the "conversions not sent" callout (the upgrade
  // engine — its recoverable count comes from the SAME backend helper the
  // upgrade backfill fires with, so the promise is always true).
  const usage = usageQuery.data;
  const isTrial = me?.effective_tier === 'trial';
  const tier =
    !isTrial && usage && usage.limit != null
      ? usageTier(usage.count, usage.limit)
      : null;

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <span className="text-2xl font-extrabold text-dark sm:text-3xl">
          Free
        </span>
        {isTrial ? (
          <span className="rounded-full bg-info/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-info">
            Trial — everything included
          </span>
        ) : (
          <span className="rounded-full bg-dark/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {PLAN_BLURB.free}
          </span>
        )}
      </div>

      {isTrial && me?.trial_ends_at && (
        <p className="mb-4 text-sm text-muted-foreground">
          <span className="font-bold text-dark">
            {daysUntil(me.trial_ends_at)} day
            {daysUntil(me.trial_ends_at) === 1 ? '' : 's'} left
          </span>{' '}
          of your trial — every contact visible, conversions sent to your ad
          platforms automatically. Ends{' '}
          {new Date(me.trial_ends_at).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
          })}
          .
        </p>
      )}

      <PlanUsageBar query={usageQuery} />

      {tier?.state === 'warn' && (
        <div className="mt-4 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <span className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />
          <p className="text-sm leading-relaxed text-amber-900">
            <span className="font-bold">
              {tier.remaining} visible contact{tier.remaining === 1 ? '' : 's'}{' '}
              left this month.
            </span>{' '}
            New contacts past that are still captured, but locked. Upgrade to
            Pro to see every contact.
          </p>
        </div>
      )}
      {tier?.state === 'over' && (
        <div className="mt-4 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <span className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />
          <p className="text-sm leading-relaxed text-amber-900">
            <span className="font-bold">
              {usage?.locked_count ?? 0} contact
              {(usage?.locked_count ?? 0) === 1 ? ' is' : 's are'} locked this
              month.
            </span>{' '}
            Tracking continues and nothing is lost. Upgrade to Pro to see them
            all.
          </p>
        </div>
      )}
      {!isTrial && (usage?.not_sent_total ?? 0) > 0 && (
        <div className="mt-4 flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
          <span className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full bg-red-500" />
          <p className="text-sm leading-relaxed text-red-900">
            <span className="font-bold">
              {usage!.not_sent_total} conversion
              {usage!.not_sent_total === 1 ? '' : 's'} not sent to your ad
              platforms.
            </span>{' '}
            {usage!.recoverable_count > 0 ? (
              <>
                {usage!.recoverable_count} can still be sent if you upgrade
                now — the rest expire as time passes.
              </>
            ) : (
              <>Upgrade to start sending new ones automatically.</>
            )}
          </p>
        </div>
      )}

      {me?.billing_provider === 'ghl' ? (
        // Billed through the CRM marketplace — never show Paddle checkout
        // (double-billing guard; plan changes happen on the app's page there).
        <p className="mt-5 rounded-2xl border border-line bg-bg-tint p-5 text-sm text-dark">
          Your plan is managed in your CRM's App Marketplace. Open the HeyMotek
          app page there to upgrade or change plans.
        </p>
      ) : processing ? (
        <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-dark">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          Activating your Pro plan…
        </p>
      ) : configured ? (
        <div className="mt-5 rounded-2xl border-[1.5px] border-brand bg-brand/5 p-5">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-base font-extrabold text-dark">Upgrade to Pro</span>
            <span className="flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-dark">
                {PRO_PRICE[billingInterval]}
                <span className="text-sm font-semibold text-muted-foreground">
                  {PRO_PERIOD[billingInterval]}
                </span>
              </span>
              {billingInterval === 'yearly' && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                  2 months free
                </span>
              )}
            </span>
          </div>
          <ul className="mb-4 space-y-1.5 text-sm text-dark">
            <li className="flex gap-2">
              <span className="font-extrabold text-brand">✓</span>
              <span>
                Unlimited contacts{' '}
                <span className="text-muted-foreground">— Free shows 100 a month</span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-extrabold text-brand">✓</span>
              <span>Dedicated support</span>
            </li>
          </ul>
          <div className="mb-4">
            <IntervalToggle value={billingInterval} onChange={setBillingInterval} />
          </div>
          <button
            type="button"
            onClick={() => openCheckout(billingInterval)}
            className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-base font-bold text-white transition-colors hover:bg-brand-dark"
          >
            Upgrade to Pro →
          </button>
        </div>
      ) : (
        // Paddle not wired yet → point at the pricing page (no broken checkout).
        <button
          type="button"
          onClick={() => window.open('https://heymotek.com/pricing/', '_blank')}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-base font-bold text-white transition-colors hover:bg-brand-dark"
        >
          Upgrade to Pro for unlimited contacts
        </button>
      )}
    </>
  );
}

function IntervalToggle({
  value,
  onChange,
}: {
  value: BillingInterval;
  onChange: (v: BillingInterval) => void;
}) {
  const opt = (v: BillingInterval, label: string) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={`rounded-full px-4 py-1.5 transition-colors ${
        value === v ? 'bg-white text-dark shadow-sm' : 'text-muted-foreground'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex rounded-full border border-dark/10 bg-dark/5 p-1 text-sm font-semibold">
      {opt('monthly', 'Monthly')}
      {opt('yearly', 'Yearly')}
    </div>
  );
}

function ProPlanBody({
  usageQuery,
}: {
  usageQuery: ReturnType<typeof useQuery<UsageResponse>>;
}) {
  const subQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => apiRequest<SubscriptionResponse>('/billing/subscription'),
    staleTime: 30_000,
  });
  // Manage/cancel/update-payment links — minted on demand via the Paddle API
  // (subscription webhooks don't reliably carry them). Refetched on focus so a
  // long-open tab doesn't hold a stale session.
  const portalQuery = useQuery({
    queryKey: ['billing', 'portal'],
    queryFn: () => apiRequest<PortalResponse>('/billing/portal'),
    staleTime: 10 * 60_000,
    retry: 1,
  });
  const sub = subQuery.data;
  const portal = portalQuery.data;
  const pastDue = sub?.payment_status === 'past_due';
  const intervalLabel =
    sub?.plan_interval === 'yearly'
      ? 'Billed yearly'
      : sub?.plan_interval === 'monthly'
        ? 'Billed monthly'
        : null;
  const updateHref = portal?.update_payment_url ?? undefined;
  const cancelHref = portal?.cancel_url ?? undefined;
  const cancelsAt = portal?.cancels_at ?? null;

  const queryClient = useQueryClient();
  const keepPlan = useMutation({
    meta: { errorMessage: "Couldn't update your plan — try again" },
    mutationFn: () => apiRequest('/billing/resume', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'portal'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
  });

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <span className="text-2xl font-extrabold text-dark sm:text-3xl">Pro</span>
        <span className="rounded-full bg-dark/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {PLAN_BLURB.pro}
        </span>
        {intervalLabel && (
          <span className="text-xs font-medium text-muted-foreground">
            {intervalLabel}
            {!cancelsAt && portal?.renews_at && (
              <>
                {' · renews '}
                {new Date(portal.renews_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </>
            )}
          </span>
        )}
      </div>

      {pastDue && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          We couldn't take your last payment. Update your payment method to keep
          Pro.
        </div>
      )}

      {cancelsAt && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p>
            Your Pro plan is set to cancel on{' '}
            <span className="font-semibold">
              {new Date(cancelsAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            . You'll keep Pro until then.
          </p>
          <button
            type="button"
            onClick={() => keepPlan.mutate()}
            disabled={keepPlan.isPending}
            className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-4 py-1.5 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-50"
          >
            {keepPlan.isPending ? 'Keeping…' : 'Keep my plan'}
          </button>
        </div>
      )}

      <PlanUsageBar query={usageQuery} />

      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
        {updateHref && (
          <a
            href={updateHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info hover:underline"
          >
            Update payment method
          </a>
        )}
        {!cancelsAt && cancelHref && (
          <a
            href={cancelHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:underline"
          >
            Cancel subscription (back to Free)
          </a>
        )}
      </div>
    </>
  );
}

function PlanUsageBar({
  query,
}: {
  query: ReturnType<typeof useQuery<UsageResponse>>;
}) {
  if (query.isLoading) {
    return (
      <SkeletonGroup>
        <SkeletonBox className="h-4 w-48" />
        <SkeletonBox className="mt-2 h-2 w-full rounded-full" />
        <SkeletonBox className="mt-2 h-3 w-32" />
      </SkeletonGroup>
    );
  }
  if (query.isError || !query.data) return null;

  const { count, limit } = query.data;

  // Pro (limit === null): unlimited — show the count for value reinforcement,
  // no denominator (per the June 2026 pricing spec).
  if (limit === null) {
    return (
      <p className="text-sm font-medium text-dark">
        {count.toLocaleString()} contacts this month
      </p>
    );
  }

  const { percent, state, remaining } = usageTier(count, limit);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-dark">
          {count} of {limit} contacts this month
        </span>
        <span className={`text-sm font-bold tabular-nums ${USAGE_TEXT[state]}`}>
          {percent}%
        </span>
      </div>
      <UsageBar count={count} limit={limit} />
      {state === 'ok' && (
        <p className="mt-2 text-xs text-muted-foreground">
          {remaining} left this month. Counter resets on the 1st.
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Notifications section — localStorage today, backend follow-on
// ──────────────────────────────────────────────────────────────────────

function NotificationsSection() {
  const [prefs, setPrefs] = useState<NotifPrefs>(DEFAULT_PREFS);

  useEffect(() => {
    setPrefs(loadPrefs());
  }, []);

  function toggle(key: keyof NotifPrefs, next: boolean) {
    const updated = { ...prefs, [key]: next };
    setPrefs(updated);
    savePrefs(updated);
  }

  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
      <div className="mb-5">
        <h2 className="text-lg font-extrabold text-dark sm:text-xl">
          Notifications
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          When we email you about your account.
        </p>
      </div>

      <div className="space-y-3">
        <NotifRow
          label="New contact"
          description="Email me when a new WhatsApp contact arrives."
          checked={prefs.new_contact}
          onChange={(next) => toggle('new_contact', next)}
        />
        <NotifRow
          label="Monthly summary"
          description="A snapshot of contacts, sales, and ROAS on the 1st."
          checked={prefs.monthly_summary}
          onChange={(next) => toggle('monthly_summary', next)}
        />
        <NotifRow
          label="Product updates"
          description="Occasional emails when we ship something worth knowing."
          checked={prefs.product_updates}
          onChange={(next) => toggle('product_updates', next)}
        />
      </div>
    </section>
  );
}

function NotifRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <Checkbox
        checked={checked}
        onCheckedChange={(c) => onChange(c === true)}
        className="mt-1"
      />
      <span className="flex-1">
        <span className="block text-sm font-semibold text-dark">{label}</span>
        <span className="block text-xs text-muted-foreground">
          {description}
        </span>
      </span>
    </label>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Danger zone
// ──────────────────────────────────────────────────────────────────────

function DangerZoneSection() {
  const navigate = useNavigate();
  const { clients } = useAuth();
  const showClient = clients.length > 1;

  const exportMutation = useMutation({
    meta: { errorMessage: "Couldn't export — try again" },
    mutationFn: async () => {
      const all: Contact[] = [];
      let cursor: string | undefined;
      // Cap at 50 pages (~5,000 contacts at PAGE_SIZE=100) so a bug or
      // huge dataset doesn't loop forever. Flag bulk-export route in
      // backend gaps when customers exceed this.
      for (let page = 0; page < 50; page += 1) {
        const params = new URLSearchParams();
        params.set('limit', '100');
        params.set('sort', 'created_at');
        params.set('order', 'desc');
        if (cursor) params.set('cursor', cursor);
        const res = await apiRequest<ContactsPage>(
          `/contacts?${params.toString()}`,
        );
        all.push(...res.items);
        if (!res.next_cursor) break;
        cursor = res.next_cursor;
      }
      exportToCsv(all, showClient);
      return all.length;
    },
  });

  async function handleSignOut() {
    await signOut(auth);
    navigate('/signin');
  }

  return (
    <section className="rounded-3xl border border-red-200 bg-card p-6 shadow-card sm:p-8">
      <h2 className="mb-1 text-lg font-extrabold text-red-700 sm:text-xl">
        Danger zone
      </h2>
      <p className="mb-5 text-sm text-muted-foreground">
        Irreversible actions. Export your data first.
      </p>

      <div className="space-y-3">
        <DangerRow
          title="Export all contacts"
          description="Download every contact + attribution data as CSV."
          actionLabel={exportMutation.isPending ? 'Preparing…' : 'Export CSV'}
          actionIcon={<Download className="h-4 w-4 text-green-700" />}
          actionVariant="green"
          actionDisabled={exportMutation.isPending}
          onAction={() => exportMutation.mutate()}
        />

        <DangerRow
          title="Sign out"
          description="Sign out on this device."
          actionLabel="Sign out"
          actionVariant="neutral"
          onAction={handleSignOut}
        />

        <DangerRow
          title="Delete account"
          description="Removes your data after a 30-day grace period."
          titleColor="red"
          actionLabel="Delete"
          actionVariant="red"
          actionDisabled
          actionTooltip="Coming soon — flagged for backend follow-on."
          onAction={() => undefined}
        />
      </div>
    </section>
  );
}

function DangerRow({
  title,
  description,
  titleColor = 'dark',
  actionLabel,
  actionIcon,
  actionVariant,
  actionDisabled = false,
  actionTooltip,
  onAction,
}: {
  title: string;
  description: string;
  titleColor?: 'dark' | 'red';
  actionLabel: string;
  actionIcon?: React.ReactNode;
  actionVariant: 'green' | 'neutral' | 'red';
  actionDisabled?: boolean;
  actionTooltip?: string;
  onAction: () => void;
}) {
  const buttonClass =
    actionVariant === 'green'
      ? 'inline-flex items-center justify-center gap-2 rounded-lg border-2 border-green-700 bg-white px-4 py-2 text-sm font-semibold text-dark transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40'
      : actionVariant === 'red'
        ? 'inline-flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40'
        : 'inline-flex items-center justify-center gap-2 rounded-lg border border-dark/15 bg-white px-4 py-2 text-sm font-semibold text-dark transition-colors hover:border-dark/30 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="flex flex-col gap-3 border-t border-red-100 pt-3 first:border-t-0 first:pt-0 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p
          className={`text-sm font-semibold ${
            titleColor === 'red' ? 'text-red-700' : 'text-dark'
          }`}
        >
          {title}
        </p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        onClick={onAction}
        disabled={actionDisabled}
        title={actionTooltip}
        className={buttonClass}
      >
        {actionIcon}
        {actionLabel}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Loading skeleton
// ──────────────────────────────────────────────────────────────────────

function AccountPageSkeleton() {
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <SkeletonGroup className="space-y-4">
        <div>
          <SkeletonBox tone="prominent" className="h-10 w-32 sm:h-12" />
          <SkeletonBox className="mt-3 h-4 w-72 max-w-full" />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBox key={i} className="h-40 w-full rounded-3xl" />
        ))}
      </SkeletonGroup>
    </div>
  );
}
